'use client'

import { useTranslation } from '@shared/config/i18n'
import React from 'react'

import type { NotionBlock, NotionPageData, RichText } from '../types/notion'

export type { NotionBlock, NotionPageData }

interface NotionRendererProps {
  blocks: NotionBlock[]
}

// Rich Text 렌더링
const RichTextRenderer: React.FC<{ richText: RichText[] }> = ({ richText }) => {
  if (!richText || richText.length === 0) {
    return null
  }

  return (
    <>
      {richText.map((text, index) => {
        const { annotations } = text
        const content = text.plain_text

        let element = <span key={index}>{content}</span>

        const linkUrl = (text.type === 'text' && text.text?.link?.url) || text.href || undefined

        if (linkUrl) {
          element = (
            <a
              key={index}
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {content}
            </a>
          )
        }

        // 스타일 적용
        if (annotations?.bold) {
          element = <strong key={index}>{element}</strong>
        }
        if (annotations?.italic) {
          element = <em key={index}>{element}</em>
        }
        if (annotations?.strikethrough) {
          element = <del key={index}>{element}</del>
        }
        if (annotations?.underline) {
          element = <u key={index}>{element}</u>
        }
        if (annotations?.code) {
          element = (
            <code key={index} className="bg-gray-100 px-[2.5px] py-[1.25px] rounded text-sm font-mono">
              {element}
            </code>
          )
        }

        return element
      })}
    </>
  )
}

// 개별 블록 렌더링
const BlockRenderer: React.FC<{ block: NotionBlock }> = ({ block }) => {
  const { type } = block

  switch (type) {
    case 'heading_1':
      if (block.heading_1) {
        return (
          <h1 className="text-3xl font-bold mt-5 mb-2.5">
            <RichTextRenderer richText={block.heading_1.rich_text} />
          </h1>
        )
      }
      return null

    case 'heading_2':
      if (block.heading_2) {
        return (
          <h2 className="text-2xl font-bold mt-[15px] mb-[7.5px]">
            <RichTextRenderer richText={block.heading_2.rich_text} />
          </h2>
        )
      }
      return null

    case 'heading_3':
      if (block.heading_3) {
        return (
          <h3 className="text-xl font-bold mt-2.5 mb-[5px]">
            <RichTextRenderer richText={block.heading_3.rich_text} />
          </h3>
        )
      }
      return null

    case 'paragraph':
      if (block.paragraph) {
        return (
          <p className="mb-2.5 leading-relaxed">
            <RichTextRenderer richText={block.paragraph.rich_text} />
          </p>
        )
      }
      return null

    case 'bulleted_list_item':
      if (block.bulleted_list_item) {
        return (
          <li className="ml-[15px] mb-[5px] list-disc">
            <RichTextRenderer richText={block.bulleted_list_item.rich_text} />
          </li>
        )
      }
      return null

    case 'numbered_list_item':
      if (block.numbered_list_item) {
        return (
          <li className="ml-[15px] mb-[5px] list-decimal">
            <RichTextRenderer richText={block.numbered_list_item.rich_text} />
          </li>
        )
      }
      return null

    case 'to_do':
      if (block.to_do) {
        return (
          <div className="flex items-start gap-[5px] mb-[5px]">
            <input type="checkbox" checked={block.to_do.checked} readOnly className="mt-[2.5px] cursor-default" />
            <span className={block.to_do.checked ? 'line-through text-gray-500' : ''}>
              <RichTextRenderer richText={block.to_do.rich_text} />
            </span>
          </div>
        )
      }
      return null

    case 'toggle':
      if (block.toggle) {
        return (
          <details className="mb-2.5">
            <summary className="font-medium cursor-pointer">
              <RichTextRenderer richText={block.toggle.rich_text} />
            </summary>
            <div className="ml-2.5 mt-[5px]">
              {block.children?.map((child: NotionBlock) => (
                <BlockRenderer key={child.id} block={child} />
              ))}
            </div>
          </details>
        )
      }
      return null

    case 'code':
      if (block.code) {
        return (
          <pre className="bg-gray-900 text-gray-100 p-2.5 rounded-[5px] overflow-x-auto mb-2.5">
            <code>
              <RichTextRenderer richText={block.code.rich_text} />
            </code>
          </pre>
        )
      }
      return null

    case 'quote':
      if (block.quote) {
        return (
          <blockquote className="border-l-4 border-gray-300 pl-2.5 italic text-gray-700 mb-2.5">
            <RichTextRenderer richText={block.quote.rich_text} />
          </blockquote>
        )
      }
      return null

    case 'callout':
      if (block.callout) {
        return (
          <div className="bg-gray-50 border border-gray-200 rounded-[5px] p-2.5 mb-2.5 flex gap-[7.5px]">
            {block.callout.icon && block.callout.icon.type === 'emoji' && (
              <span className="text-2xl">{block.callout.icon.emoji}</span>
            )}
            <div>
              <RichTextRenderer richText={block.callout.rich_text} />
            </div>
          </div>
        )
      }
      return null

    case 'divider':
      return <hr className="my-5 border-gray-300" />

    case 'image':
      if (block.image) {
        const imageUrl = block.image.file?.url || block.image.external?.url
        return (
          <figure className="my-2.5">
            {imageUrl && <img src={imageUrl} alt="Notion image" className="w-full rounded-[5px]" />}
            {block.image.caption && block.image.caption.length > 0 && (
              <figcaption className="text-sm text-gray-600 text-center mt-[5px]">
                <RichTextRenderer richText={block.image.caption} />
              </figcaption>
            )}
          </figure>
        )
      }
      return null

    case 'video':
      if (block.video) {
        const videoUrl = block.video.file?.url || block.video.external?.url
        return (
          <div className="my-2.5">
            {videoUrl && (
              <video controls className="w-full rounded-[5px]">
                <source src={videoUrl} />
              </video>
            )}
          </div>
        )
      }
      return null

    case 'file':
      if (block.file) {
        const fileUrl = block.file.file?.url || block.file.external?.url
        const fileName = block.file.name || 'Download file'
        return (
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-[5px] text-blue-600 hover:underline mb-2.5"
          >
            📎 {fileName}
          </a>
        )
      }
      return null

    case 'bookmark':
      if (block.bookmark) {
        return (
          <a
            href={block.bookmark.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block border border-gray-300 rounded-[5px] p-2.5 hover:bg-gray-50 mb-2.5"
          >
            🔗 {block.bookmark.url}
          </a>
        )
      }
      return null

    default:
      // 지원하지 않는 블록 타입
      return null
  }
}

// 메인 렌더러
export const NotionRenderer: React.FC<NotionRendererProps> = ({ blocks }) => {
  const t = useTranslation()

  if (!blocks || blocks.length === 0) {
    return <p className="text-gray-500">{t('common.empty.content')}</p>
  }

  // 연속된 list 아이템들을 그룹화
  const groupedBlocks: (NotionBlock | NotionBlock[])[] = []
  let currentListItems: NotionBlock[] = []
  let currentListType: 'bulleted' | 'numbered' | null = null

  blocks.forEach((block) => {
    if (block.type === 'bulleted_list_item') {
      if (currentListType !== 'bulleted' && currentListItems.length > 0) {
        groupedBlocks.push([...currentListItems])
        currentListItems = []
      }
      currentListType = 'bulleted'
      currentListItems.push(block)
    } else if (block.type === 'numbered_list_item') {
      if (currentListType !== 'numbered' && currentListItems.length > 0) {
        groupedBlocks.push([...currentListItems])
        currentListItems = []
      }
      currentListType = 'numbered'
      currentListItems.push(block)
    } else {
      if (currentListItems.length > 0) {
        groupedBlocks.push([...currentListItems])
        currentListItems = []
        currentListType = null
      }
      groupedBlocks.push(block)
    }
  })

  // 마지막 리스트 아이템 추가
  if (currentListItems.length > 0) {
    groupedBlocks.push(currentListItems)
  }

  return (
    <div className="notion-content max-w-4xl mx-auto px-2.5 py-5">
      {groupedBlocks.map((item, index) => {
        if (Array.isArray(item)) {
          // 리스트 그룹
          const ListTag = item[0].type === 'bulleted_list_item' ? 'ul' : 'ol'
          return (
            <ListTag key={index} className="mb-2.5">
              {item.map((block) => (
                <BlockRenderer key={block.id} block={block} />
              ))}
            </ListTag>
          )
        } else {
          // 단일 블록
          return <BlockRenderer key={item.id} block={item} />
        }
      })}
    </div>
  )
}
